// SPDX-License-Identifier: AGPL-3.0-or-later

import {TextualAttachmentCodePanel} from '@app/features/channel/components/embeds/attachments/TextualAttachmentCodePanel';
import styles from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreviewBottomSheet.module.css';
import {
	getAttachmentFileName,
	getLineCount,
	type TextualAttachmentPreviewModalProps,
} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreviewUtils';
import TextualPreview from '@app/features/messaging/state/TextualPreview';
import {downloadFile} from '@app/features/messaging/utils/FileDownloadUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {DotsThreeIcon, DownloadSimpleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo, useState} from 'react';
export const TextualAttachmentPreviewBottomSheet = observer(function TextualAttachmentPreviewBottomSheet({
	attachment,
	highlightedHtml,
	onClose,
	previewError,
	status,
	textContent,
	wrapText,
}: TextualAttachmentPreviewModalProps) {
	const {i18n} = useLingui();
	const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
	const lineCount = useMemo(() => getLineCount(textContent), [textContent]);
	const visibleLineCount = useMemo(() => Math.max(lineCount, 1), [lineCount]);
	const fileName = getAttachmentFileName(attachment);
	let fileSizeLabel = '';
	if (typeof attachment.size === 'number') {
		fileSizeLabel = formatFileSize(attachment.size);
	}
	const handleDownload = useCallback(async () => {
		const downloadUrl = attachment.proxy_url ?? attachment.url;
		if (!downloadUrl) {
			return;
		}
		await downloadFile(downloadUrl, 'file', fileName || 'file');
	}, [attachment.proxy_url, attachment.url, fileName]);
	const handleOpenMoreOptions = useCallback(() => {
		setMoreOptionsOpen(true);
	}, []);
	const handleCloseMoreOptions = useCallback(() => {
		setMoreOptionsOpen(false);
	}, []);
	const moreOptionsGroups = useMemo<Array<MenuGroupType>>(
		() => [
			{
				items: [
					{
						icon: <DownloadSimpleIcon size={20} />,
						label: i18n._(msg`Download`),
						onClick: handleDownload,
					},
					{
						icon: undefined,
						label: i18n._(msg`Wrap text`),
						checked: wrapText,
						onChange: TextualPreview.toggleWrapText,
					},
				],
			},
		],
		[handleDownload, i18n, wrapText],
	);
	return (
		<>
			<BottomSheet
				isOpen
				onClose={onClose}
				snapPoints={[0, 1]}
				initialSnap={1}
				title={i18n._(msg`Attachment preview`)}
				disablePadding
			>
				<div className={styles.container}>
					<div className={styles.codeContainer}>
						<TextualAttachmentCodePanel
							canExpand
							fillAvailableSpace
							highlightedHtml={highlightedHtml}
							isExpanded
							previewError={previewError}
							status={status}
							textContent={textContent}
							visibleLineCount={visibleLineCount}
							wrapText={wrapText}
							wrapperClassName={styles.codeSurface}
						/>
					</div>
					<div className={styles.footer}>
						<div className={styles.fileSection}>
							<div className={styles.fileMeta}>
								<span className={styles.fileName}>{fileName}</span>
								{fileSizeLabel && <span className={styles.fileSize}>{fileSizeLabel}</span>}
							</div>
						</div>
						<div className={styles.footerActions}>
							<button
								type="button"
								className={styles.actionButton}
								onClick={handleDownload}
								aria-label={i18n._(msg`Download`)}
							>
								<DownloadSimpleIcon size={20} weight="regular" />
							</button>
							<button
								type="button"
								className={styles.actionButton}
								onClick={handleOpenMoreOptions}
								aria-label={i18n._(msg`More Options`)}
							>
								<DotsThreeIcon size={20} weight="bold" />
							</button>
						</div>
					</div>
				</div>
			</BottomSheet>
			<MenuBottomSheet
				isOpen={moreOptionsOpen}
				onClose={handleCloseMoreOptions}
				title={i18n._(msg`Options`)}
				groups={moreOptionsGroups}
			/>
		</>
	);
});
